import { CommonModule } from '@angular/common';
import { Component, computed, Inject, inject, Optional, Signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatMenuModule } from '@angular/material/menu';
import { Focus, FocusAttemptData } from '@app/_interfaces/focus-trainer.interface';
import { CacheService } from '@app/_services/cache.service';
import { SettingsService } from '@app/_services/settings.service';
import { FocusStore } from '@app/_stores';
import { CollaboraterRole, CollaboraterStatus } from '@app/_types/generic.type';
import { CollaboratorComponent } from '@app/components/addons/collaborators/collaborator/collaborator.component';
import { FeatureGuideDialogComponent } from '@app/components/common/feature-guides/feature-guide-dialog/feature-guide-dialog.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { FocusLevelComponent } from '../focus-level/focus-level.component';
import { AlertService } from '@app/_services/alert.service';
import { SecondsToTimePipe } from '@app/_pipes/seconds-to-time.pipe';
import { CommentsListComponent } from '@app/components/addons/comments/comments-list/comments-list.component';
import { FirestoreCollection } from '@app/_enums/firestore-collection.enum';
import { getLocalDateString as getDateString, toDate } from '@app/_services/date';
import { log } from '@app/_services/logger';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { ModalService } from '@app/_services/modal.service';
import { CollaboratorService } from '@app/_services/collaborator.service';
import { BlockedUserService } from '@app/_services/blocked-user.service';
import { getRoleByUid, getCollaboratorCount } from '@app/_utils/members-model.utils';
import * as htmlToImage from 'html-to-image';
import { TimeFormatPipe } from "../../../../_pipes/time-format.pipe";

@Component({
  selector: 'app-focus-scores',
  imports: [
    CommonModule,
    MatMenuModule,
    SvgComponent,
    SecondsToTimePipe,
    TimeFormatPipe
],
  templateUrl: './focus-scores.component.html',
  styleUrl: './focus-scores.component.scss'
})

export class FocusScoresComponent {

  readonly focusStore = inject(FocusStore);
  focus: Signal<Focus> = computed(() => {
    return this.focusStore.focusMap()[this.data.focusId];
  });
  role: Signal<CollaboraterRole> = computed(() => {
    return this.focus().uid === this.cc.user.uid ? 'owner' : getRoleByUid(this.focus().members, this.cc.user.uid) || 'viewer';
  });

  // Check if the entity owner is blocked (for Block/Unblock button)
  isOwnerBlocked: Signal<boolean> = computed(() => {
    const ownerEmail = this.focus().members?.ownerEmail;
    if (!ownerEmail) return false;
    return this.blockedUserService.isBlocked(ownerEmail);
  });
  collabCount: Signal<number> = computed(() => {
    return getCollaboratorCount(this.focus().members);
  });

  attempts: Signal<FocusAttemptData[]> = computed(() => {
    return this.focus().attempts ? Object.values(this.focus().attempts) || [] : [];
  });

  dateString: Signal<string> = computed(() => {
    return this.data.dateString;
  });

  relativeAttempts: Signal<FocusAttemptData[]> = computed(() => {
    if (!this.data.dateString) return [];
    const allAttempts = this.attempts();
    const attempts: FocusAttemptData[] = Object.values(allAttempts);
    return attempts
      .filter(a => getDateString(toDate(a.startAt)) === this.data.dateString)
      .sort((a, b) => {
        const dateB = toDate(b.startAt);
        const dateA = toDate(a.startAt);
        return (dateB?.getTime() ?? 0) - (dateA?.getTime() ?? 0);
      });
  });

  highestAttempt: Signal<{
    score: number;
    shapeCount: number;
    level: number;
    duration: number;
    id: string;
    startAt: Date;
  }> = computed(() => {

    const attempts: FocusAttemptData[] = this.relativeAttempts();
    if (attempts.length === 0) {
      return { score: 0, shapeCount: 0, level: 1, duration: 0, id: 'no', startAt: new Date() };
    }

    const highestAttempt = attempts.reduce((a, b) => {
      const scoreA = a.shapeCount * a.level;
      const scoreB = b.shapeCount * b.level;

      if (scoreA > scoreB) return a;
      if (scoreB > scoreA) return b;

      // 🔥 SAME SCORE → pick latest startAt
      return a.startAt > b.startAt ? a : b;
    });

    return {
      score: highestAttempt.shapeCount * highestAttempt.level,
      shapeCount: highestAttempt.shapeCount,
      level: highestAttempt.level,
      duration: highestAttempt.shapeCount * 6,
      id: highestAttempt.id,
      startAt: highestAttempt.startAt,
    };
  });

  todayDateString: Signal<string> = computed(() => {
    return this.cc.today().dateString;
  });

  public data: { focusId: string, dateString: string };

  constructor(
    public ss: SettingsService,
    public cc: CacheService,
    @Optional() public dialogRef: MatDialogRef<FocusScoresComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<FocusScoresComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: { focusId: string, dateString: string } | null,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) bottomSheetData: { focusId: string, dateString: string } | null,
    public dialog: MatDialog,
    private alertService: AlertService,
    private modalService: ModalService,
    private collaboratorService: CollaboratorService,
    private blockedUserService: BlockedUserService

  ) {
    this.data = (dialogData || bottomSheetData)!;
    log.debug('constructor', 'Focus data loaded', { focus: this.focus(), relativeAttempts: this.relativeAttempts(), highestAttempt: this.highestAttempt() });
  }

  async deleteAttempt(attempt: FocusAttemptData) {
    const allAttempts = { ...this.focus().attempts };
    delete allAttempts[attempt.id];
    const updatedFocus: Focus = {
      ...this.focus(),
      attempts: allAttempts,
    };
    await this.focusStore.updateFocus(updatedFocus, true, this.data.dateString);
    const attempts = this.focus().attempts ? Object.values(this.focus().attempts) : [];
    const relativeAttempts = attempts.filter(a => getDateString(toDate(a.startAt)) === this.data.dateString);
    if (relativeAttempts.length === 0) {
      this.closeDialog();
    }
  };

  isCollab: Signal<boolean> = computed(() => {
    return this.collabCount() > 0;
  });

  addCollaborator() {
    this.modalService.openLevel2(
      CollaboratorComponent,
      {
        collection: 'focus',
        entityData: this.focus(),
        isPublic: false,
      },
      { disableClose: true }
    );
  };

  async removeCollab(action: CollaboraterStatus) {
    if (action === 'left') {
      const res = await this.alertService.confirm(
        this.ss.texts()['leaveFocusTracker_title'],
        this.ss.texts()['leaveFocusTracker_content'],
        this.ss.texts()['common_leave'],
        this.ss.texts()['common_buttonCancel'],
        'color-11', 'color-7'
      );
      if (!res?.confirm) return;
      try {
        this.cc.isLoading.set(true);
        const success = await this.collaboratorService.removeMemberFromEntity(
          this.focus().id, 'focus', this.cc.user.email
        );
        if (!success) throw new Error('Failed to remove member from entity');
        this.closeDialog();
      } catch (error) {
        log.error('removeCollab', 'Failed to leave focus tracker', error as Error);
        this.alertService.alert(this.ss.texts()['common_error'], this.ss.texts()['focusTracker_leave_error']);
      } finally {
        this.cc.isLoading.set(false);
      }
    } else if (action === 'blocked') {
      const ownerEmail = this.focus().members?.ownerEmail;
      if (!ownerEmail) return;
      await this.blockedUserService.toggleBlockWithConfirmation(ownerEmail, 'focus');
    }
  };

  openComments() {
    const commentsDialog = this.modalService.openLevel2(
      CommentsListComponent,
      {
        entityId: this.focus().id,
        collection: FirestoreCollection.Focus,
        members: this.focus().members,
        entityUid: this.focus().uid,
        newEntity: null
      },
      { disableClose: true }
    );
  };

  openFocusLevelSelect() {
    this.modalService.openLevel3(FocusLevelComponent, { id: 'focusLevelDialog' })
  };

  openFeatureInfo() {
    const setupDialog = this.modalService.openLevel1(
      FeatureGuideDialogComponent,
      {
        entityType: 'focus'
      },
      { disableClose: true, autoFocus: false }
    );
  };

  closeDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    }
  };

  downloadImage() {
    const highestAttempt = this.highestAttempt();
    const score = highestAttempt.score;
    const totalMinutes = (highestAttempt.duration / 60).toFixed(2);
    const userName = this.cc.user.name || this.cc.user.email?.split('@')[0] || '';
    const durationText = this.ss.interpolateText('focus_durationInMinute', { minute: totalMinutes });

    // 1. Create off-screen wrapper
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.left = '-9999px';
    wrapper.style.top = '0';
    wrapper.style.width = '308px';
    document.body.appendChild(wrapper);

    // 2. Outer container (card background)
    const container = document.createElement('div');
    container.style.width = '100%';
    container.style.backgroundColor = this.ss.theme().color5;
    container.style.borderRadius = '24px';
    container.style.padding = '16px';
    container.style.boxSizing = 'border-box';
    container.style.fontFamily = 'Roboto, sans-serif';
    wrapper.appendChild(container);

    // 3. Inner colored box (score section)
    const scoreBox = document.createElement('div');
    scoreBox.style.width = '100%';
    scoreBox.style.backgroundColor = this.ss.theme().color1;
    scoreBox.style.borderRadius = '12px';
    scoreBox.style.padding = '20px';
    scoreBox.style.boxSizing = 'border-box';
    scoreBox.style.textAlign = 'center';
    container.appendChild(scoreBox);

    // Title: "Focus Score"
    const title = document.createElement('p');
    title.innerText = this.ss.texts()['focus_focusScore'];
    title.style.font = '18px/24px Roboto, sans-serif';
    title.style.fontWeight = '700';
    title.style.color = this.ss.theme().color12;
    title.style.margin = '0 0 8px 0';
    scoreBox.appendChild(title);

    // Username
    const nameEl = document.createElement('p');
    nameEl.innerText = userName;
    nameEl.style.font = '12px/16px Roboto, sans-serif';
    nameEl.style.color = this.ss.theme().color12;
    nameEl.style.margin = '0 0 20px 0';
    scoreBox.appendChild(nameEl);

    // Big score number
    const scoreEl = document.createElement('p');
    scoreEl.innerText = score.toString();
    scoreEl.style.font = '48px/56px Roboto, sans-serif';
    scoreEl.style.fontWeight = '800';
    scoreEl.style.color = this.ss.theme().color12;
    scoreEl.style.margin = '0 0 16px 0';
    scoreBox.appendChild(scoreEl);

    // Duration in minutes
    const durationEl = document.createElement('p');
    durationEl.innerText = durationText;
    durationEl.style.font = '12px/16px Roboto, sans-serif';
    durationEl.style.color = this.ss.theme().color12;
    durationEl.style.margin = '0';
    scoreBox.appendChild(durationEl);

    // 4. Footer section
    const footer = document.createElement('div');
    footer.style.paddingTop = '16px';
    container.appendChild(footer);

    // Footer top row: "Focus Tracker" + "Free" badge
    const footerTop = document.createElement('div');
    footerTop.style.display = 'flex';
    footerTop.style.justifyContent = 'space-between';
    footerTop.style.alignItems = 'center';
    footer.appendChild(footerTop);

    const focusTrackerLabel = document.createElement('p');
    focusTrackerLabel.innerText = this.ss.texts()['common_focusTracker'];
    focusTrackerLabel.style.font = '18px/24px Roboto, sans-serif';
    focusTrackerLabel.style.fontWeight = '700';
    focusTrackerLabel.style.color = this.ss.theme().color8;
    focusTrackerLabel.style.margin = '0';
    footerTop.appendChild(focusTrackerLabel);

    const freeBadge = document.createElement('span');
    freeBadge.innerText = this.ss.texts()['common_subscriptionFree'];
    freeBadge.style.font = '14px/18px Roboto, sans-serif';
    freeBadge.style.fontWeight = '700';
    freeBadge.style.color = this.ss.theme().color12;
    freeBadge.style.backgroundColor = this.ss.theme().color1;
    freeBadge.style.borderRadius = '12px';
    freeBadge.style.padding = '2px 16px';
    footerTop.appendChild(freeBadge);

    // Footer bottom row: website URL
    const footerBottom = document.createElement('div');
    footerBottom.style.display = 'flex';
    footerBottom.style.justifyContent = 'flex-end';
    footerBottom.style.paddingTop = '16px';
    footer.appendChild(footerBottom);

    const url = document.createElement('p');
    url.innerText = 'www.mevolve.app';
    url.style.font = '14px/18px Roboto, sans-serif';
    url.style.fontWeight = '700';
    url.style.color = this.ss.theme().color7;
    url.style.margin = '0';
    footerBottom.appendChild(url);

    // 5. Convert to image and download
    htmlToImage.toPng(container, {
      backgroundColor: this.ss.theme().color5,
      pixelRatio: 2,
      skipFonts: false,
    })
      .then(dataUrl => {
        const link = document.createElement('a');
        link.download = 'focus-score.png';
        link.href = dataUrl;
        link.click();
        this.alertService.toast('imageDownloaded_content');
        document.body.removeChild(wrapper);
      })
      .catch(err => {
        console.error('Image conversion failed:', err);
        document.body.removeChild(wrapper);
      });
  };
}
